import React, { useMemo, useState } from "react";

type TokensResponse = {
  accessToken: string;
  refreshToken?: string;
};

type SupportImpersonationResult = TokensResponse & {
  supportImpersonation: true;
  sessionId: string;
  targetUserId: string;
  targetEmail: string | null;
  expiresAt: string;
};

type SupportUserAuthSummary = {
  id: string;
  authType: string;
  authIdentifier: string | null;
  emailVerifiedAt: string | null;
};

type SupportUserSessionSummary = {
  sessionId: string;
  authId: string;
  authType: string | null;
  authIdentifier: string | null;
  ipAddress: string | null;
  city: string | null;
  region: string | null;
  country: string | null;
  platform: string | null;
  deviceName: string | null;
  model: string | null;
  userAgent: string | null;
  lastUsedAt: string | null;
  refreshTokenExpiresAt: string;
  revokedAt: string | null;
  isSupportImpersonation: boolean;
  supportAdminEmail: string | null;
};

type SupportUserSearchItem = {
  id: string;
  email: string | null;
  username: string;
  discriminator: string;
  isActive: boolean;
  membershipType: string | null;
  createDate: string;
  updateDate: string;
  auths: SupportUserAuthSummary[];
  recentSessions: SupportUserSessionSummary[];
};

type SupportUserSearchResult = {
  query: string;
  users: SupportUserSearchItem[];
};

const storageKeys = {
  apiBase: "youwo-support-access-api-base",
  adminToken: "youwo-support-access-admin-token",
};

const normalizeBase = (value: string) => value.trim().replace(/\/+$/, "");

const getDefaultApiBase = () => {
  const configured =
    process.env.REACT_APP_API_ENDPOINT || process.env.REACT_APP_API_URL || "";
  if (configured) return normalizeBase(configured);

  const host = window.location.hostname;
  if (host.includes("dev")) return "https://api-server.dev.youwo.ai";
  return "https://api-server.youwo.ai";
};

const readSession = (key: string, fallback: string) => {
  try {
    return sessionStorage.getItem(key) || fallback;
  } catch {
    return fallback;
  }
};

const writeSession = (key: string, value: string) => {
  try {
    if (value) {
      sessionStorage.setItem(key, value);
    } else {
      sessionStorage.removeItem(key);
    }
  } catch {
    // Session storage can be unavailable in private/browser-restricted modes.
  }
};

const decodeJwtExpiryMs = (token: string): number | null => {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const decoded = JSON.parse(
      window.atob(payload.replace(/-/g, "+").replace(/_/g, "/")),
    );
    return decoded?.exp ? decoded.exp * 1000 : null;
  } catch {
    return null;
  }
};

const formatJwtExpiry = (token: string): string | null => {
  const expiryMs = decodeJwtExpiryMs(token);
  return expiryMs ? new Date(expiryMs).toLocaleString() : null;
};

const requestJson = async <T,>(
  url: string,
  init: RequestInit,
): Promise<T> => {
  const response = await fetch(url, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init.headers || {}),
    },
  });
  const text = await response.text();
  const data = text ? JSON.parse(text) : null;
  if (!response.ok) {
    const message =
      data?.message ||
      data?.error?.message ||
      data?.error ||
      `${response.status} ${response.statusText}`;
    throw new Error(typeof message === "string" ? message : JSON.stringify(message));
  }
  return data as T;
};

const buildAuthStateSnippet = (tokens: TokensResponse) => {
  const accessTokenExpiresAt =
    decodeJwtExpiryMs(tokens.accessToken) || Date.now() + 15 * 60 * 1000;
  const refreshTokenExpiresAt = tokens.refreshToken
    ? decodeJwtExpiryMs(tokens.refreshToken) || Date.now() + 30 * 60 * 1000
    : Date.now() + 30 * 60 * 1000;
  const deviceId = "support-access-web";
  const deviceInfo = {
    deviceId,
    platform: "web",
    model: "Support Access",
    osVersion: navigator.userAgent.slice(0, 120),
  };
  const state = {
    isLoggedIn: JSON.stringify(true),
    tokens: JSON.stringify({
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken || "",
      accessTokenExpiresAt,
      refreshTokenExpiresAt,
    }),
    isLoading: JSON.stringify(false),
    error: JSON.stringify(null),
    isGuest: JSON.stringify(false),
    deviceInfo: JSON.stringify(deviceInfo),
    isLoggingOut: JSON.stringify(false),
    _persist: JSON.stringify({ version: -1, rehydrated: true }),
  };
  const persistedAuth = JSON.stringify(state);
  return `(() => {
  const authValue = ${JSON.stringify(persistedAuth)};
  const deviceId = ${JSON.stringify(deviceId)};
  localStorage.setItem("secureStorage\\\\persist:secure_auth", authValue);
  localStorage.setItem("persist:secure_auth", authValue);
  localStorage.setItem("secureStorage\\\\deviceId", deviceId);
  localStorage.setItem("deviceId", deviceId);
  location.href = "/";
})();`;
};

const copyText = async (value: string) => {
  await navigator.clipboard.writeText(value);
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  border: "1px solid #d6d9df",
  borderRadius: 6,
  padding: "10px 12px",
  fontSize: 14,
  lineHeight: "20px",
  outline: "none",
  background: "#fff",
};

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 12,
  fontWeight: 700,
  color: "#4b5563",
  marginBottom: 6,
};

const buttonStyle: React.CSSProperties = {
  border: "1px solid #111827",
  borderRadius: 6,
  background: "#111827",
  color: "#fff",
  padding: "10px 14px",
  fontSize: 13,
  fontWeight: 700,
  cursor: "pointer",
};

const secondaryButtonStyle: React.CSSProperties = {
  ...buttonStyle,
  border: "1px solid #cfd4dc",
  background: "#fff",
  color: "#111827",
};

const panelStyle: React.CSSProperties = {
  background: "#fff",
  border: "1px solid #e1e5ea",
  borderRadius: 8,
  padding: 20,
};

const smallTextStyle: React.CSSProperties = {
  fontSize: 12,
  lineHeight: "18px",
  color: "#6b7280",
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "never";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const describeSession = (session: SupportUserSessionSummary) => {
  const device =
    session.deviceName || session.model || session.platform || session.userAgent || "unknown";
  const place = [session.city, session.region, session.country].filter(Boolean).join(", ");
  return `${device}${place ? ` · ${place}` : ""}`;
};

export default function SupportAccess() {
  const searchParams = new URLSearchParams(window.location.search);
  const key = searchParams.get("key") || "";
  const [apiBase, setApiBase] = useState(() =>
    readSession(storageKeys.apiBase, getDefaultApiBase()),
  );
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [adminAccessToken, setAdminAccessToken] = useState(() =>
    readSession(storageKeys.adminToken, ""),
  );
  const [targetEmail, setTargetEmail] = useState(
    searchParams.get("email") || searchParams.get("targetEmail") || "",
  );
  const [targetUserId, setTargetUserId] = useState(
    searchParams.get("userId") || searchParams.get("targetUserId") || "",
  );
  const [userSearch, setUserSearch] = useState(
    searchParams.get("search") || searchParams.get("email") || "",
  );
  const [searchResults, setSearchResults] = useState<SupportUserSearchItem[]>([]);
  const [selectedUser, setSelectedUser] = useState<SupportUserSearchItem | null>(null);
  const [reason, setReason] = useState("support account access");
  const [expiresInMinutes, setExpiresInMinutes] = useState(30);
  const [result, setResult] = useState<SupportImpersonationResult | null>(null);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [isWorking, setIsWorking] = useState(false);

  const normalizedApiBase = useMemo(() => normalizeBase(apiBase), [apiBase]);
  const adminTokenExpiry = useMemo(
    () => formatJwtExpiry(adminAccessToken),
    [adminAccessToken],
  );
  const targetTokenExpiry = useMemo(
    () => (result?.accessToken ? formatJwtExpiry(result.accessToken) : null),
    [result],
  );
  const authStateSnippet = useMemo(
    () => (result ? buildAuthStateSnippet(result) : ""),
    [result],
  );

  const setStoredApiBase = (value: string) => {
    setApiBase(value);
    writeSession(storageKeys.apiBase, value);
  };

  const setStoredAdminToken = (value: string) => {
    setAdminAccessToken(value);
    writeSession(storageKeys.adminToken, value);
  };

  const handleAdminLogin = async () => {
    setIsWorking(true);
    setError("");
    setStatus("");
    try {
      const tokens = await requestJson<TokensResponse>(
        `${normalizedApiBase}/auth/local/login`,
        {
          method: "POST",
          body: JSON.stringify({
            email: adminEmail.trim().toLowerCase(),
            password: adminPassword,
          }),
        },
      );
      setStoredAdminToken(tokens.accessToken);
      setAdminPassword("");
      setStatus("Admin token loaded.");
    } catch (nextError: any) {
      setError(nextError.message || "Admin login failed");
    } finally {
      setIsWorking(false);
    }
  };

  const selectUser = (user: SupportUserSearchItem) => {
    setSelectedUser(user);
    setTargetUserId(user.id);
    setTargetEmail(user.email || "");
    setReason(`support access for ${user.email || user.username}`);
    setResult(null);
    setStatus(`Selected ${user.email || user.username}.`);
  };

  const handleSearchUsers = async () => {
    setIsWorking(true);
    setError("");
    setStatus("");
    setResult(null);
    try {
      if (!adminAccessToken.trim()) {
        throw new Error("Admin access token is required.");
      }
      if (userSearch.trim().length < 2) {
        throw new Error("Search by email, username, user id, or session id.");
      }

      const response = await requestJson<SupportUserSearchResult>(
        `${normalizedApiBase}/auth/support/users?${new URLSearchParams({
          query: userSearch.trim(),
          limit: "15",
        }).toString()}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${adminAccessToken.trim()}`,
          },
        },
      );

      setSearchResults(response.users);
      if (response.users.length === 1) {
        selectUser(response.users[0]);
      } else {
        setSelectedUser(null);
        setStatus(`${response.users.length} user match${response.users.length === 1 ? "" : "es"}.`);
      }
    } catch (nextError: any) {
      setError(nextError.message || "User search failed");
    } finally {
      setIsWorking(false);
    }
  };

  const handleImpersonate = async () => {
    setIsWorking(true);
    setError("");
    setStatus("");
    setResult(null);
    try {
      if (!adminAccessToken.trim()) {
        throw new Error("Admin access token is required.");
      }
      if (!targetEmail.trim() && !targetUserId.trim()) {
        throw new Error("Enter a target email or target user id.");
      }
      const body: Record<string, unknown> = {
        reason,
        expiresInMinutes,
      };
      if (targetEmail.trim()) body.targetEmail = targetEmail.trim().toLowerCase();
      if (targetUserId.trim()) body.targetUserId = targetUserId.trim();

      const supportSession = await requestJson<SupportImpersonationResult>(
        `${normalizedApiBase}/auth/support/impersonate`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${adminAccessToken.trim()}`,
          },
          body: JSON.stringify(body),
        },
      );
      setResult(supportSession);
      setStatus("Target support session created.");
    } catch (nextError: any) {
      setError(nextError.message || "Support session failed");
    } finally {
      setIsWorking(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f6f7f9",
        color: "#111827",
        fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
      }}
    >
      <header style={{ background: "#fff", borderBottom: "1px solid #e1e5ea" }}>
        <div
          style={{
            maxWidth: 1180,
            margin: "0 auto",
            padding: "18px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
          }}
        >
          <div>
            <h1 style={{ margin: 0, fontSize: 18, lineHeight: "24px" }}>
              Admin Support
            </h1>
            <div style={{ marginTop: 2, fontSize: 12, color: "#6b7280" }}>
              Search users and create audited support sessions.
            </div>
          </div>
          <a
            href={`/internal/dashboard?key=${key}`}
            style={{ fontSize: 13, color: "#2563eb", textDecoration: "none" }}
          >
            Internal dashboard
          </a>
        </div>
      </header>

      <main
        style={{
          maxWidth: 1180,
          margin: "0 auto",
          padding: "24px",
          display: "grid",
          gridTemplateColumns: "minmax(0, 420px) minmax(0, 1fr)",
          gap: 20,
        }}
      >
        <section style={{ display: "grid", gap: 16 }}>
          <div style={panelStyle}>
            <h2 style={{ margin: "0 0 16px", fontSize: 15 }}>Admin token</h2>
            <div style={{ display: "grid", gap: 12 }}>
              <label>
                <span style={labelStyle}>API base</span>
                <input
                  value={apiBase}
                  onChange={(event) => setStoredApiBase(event.target.value)}
                  style={inputStyle}
                />
              </label>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                  gap: 10,
                }}
              >
                <label>
                  <span style={labelStyle}>Admin email</span>
                  <input
                    value={adminEmail}
                    onChange={(event) => setAdminEmail(event.target.value)}
                    autoComplete="username"
                    style={inputStyle}
                  />
                </label>
                <label>
                  <span style={labelStyle}>Admin password</span>
                  <input
                    value={adminPassword}
                    onChange={(event) => setAdminPassword(event.target.value)}
                    type="password"
                    autoComplete="current-password"
                    style={inputStyle}
                  />
                </label>
              </div>
              <button
                onClick={handleAdminLogin}
                disabled={isWorking || !adminEmail.trim() || !adminPassword}
                style={{
                  ...buttonStyle,
                  opacity: isWorking || !adminEmail.trim() || !adminPassword ? 0.55 : 1,
                }}
              >
                Log in admin
              </button>
              <label>
                <span style={labelStyle}>Or paste admin access token</span>
                <textarea
                  value={adminAccessToken}
                  onChange={(event) => setStoredAdminToken(event.target.value)}
                  style={{ ...inputStyle, height: 92, fontFamily: "monospace" }}
                />
              </label>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  style={secondaryButtonStyle}
                  onClick={() => copyText(adminAccessToken)}
                  disabled={!adminAccessToken}
                >
                  Copy admin token
                </button>
                <button
                  style={secondaryButtonStyle}
                  onClick={() => setStoredAdminToken("")}
                  disabled={!adminAccessToken}
                >
                  Clear token
                </button>
              </div>
              {adminAccessToken && (
                <div style={{ fontSize: 12, color: "#4b5563" }}>
                  Admin token expires: {adminTokenExpiry || "unknown"}
                </div>
              )}
            </div>
          </div>

          <div style={panelStyle}>
            <h2 style={{ margin: "0 0 16px", fontSize: 15 }}>User search</h2>
            <div style={{ display: "grid", gap: 12 }}>
              <label>
                <span style={labelStyle}>Email, username, user id, or session id</span>
                <input
                  value={userSearch}
                  onChange={(event) => setUserSearch(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      handleSearchUsers();
                    }
                  }}
                  placeholder="preston@example.com"
                  style={inputStyle}
                />
              </label>
              <button
                onClick={handleSearchUsers}
                disabled={isWorking || !adminAccessToken.trim()}
                style={{
                  ...buttonStyle,
                  opacity: isWorking || !adminAccessToken.trim() ? 0.55 : 1,
                }}
              >
                Search users
              </button>

              {selectedUser && (
                <div
                  style={{
                    border: "1px solid #dbe3ef",
                    borderRadius: 8,
                    padding: 12,
                    background: "#f8fafc",
                    display: "grid",
                    gap: 8,
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 700 }}>
                    {selectedUser.email || selectedUser.username}
                  </div>
                  <div style={smallTextStyle}>
                    {selectedUser.id} · {selectedUser.isActive ? "active" : "inactive"} ·{" "}
                    {selectedUser.membershipType || "no membership"}
                  </div>
                </div>
              )}

              <label>
                <span style={labelStyle}>Target email</span>
                <input
                  value={targetEmail}
                  onChange={(event) => setTargetEmail(event.target.value)}
                  placeholder="Optional if user id is set"
                  style={inputStyle}
                />
              </label>
              <label>
                <span style={labelStyle}>Target user id</span>
                <input
                  value={targetUserId}
                  onChange={(event) => setTargetUserId(event.target.value)}
                  placeholder="Selected from search"
                  style={inputStyle}
                />
              </label>
              <label>
                <span style={labelStyle}>Reason</span>
                <input
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  style={inputStyle}
                />
              </label>
              <label>
                <span style={labelStyle}>Expires in minutes</span>
                <input
                  value={expiresInMinutes}
                  onChange={(event) =>
                    setExpiresInMinutes(Number(event.target.value) || 30)
                  }
                  min={5}
                  max={120}
                  type="number"
                  style={inputStyle}
                />
              </label>
              <button
                onClick={handleImpersonate}
                disabled={isWorking || !adminAccessToken.trim()}
                style={{
                  ...buttonStyle,
                  opacity: isWorking || !adminAccessToken.trim() ? 0.55 : 1,
                }}
              >
                Create support session
              </button>
            </div>
          </div>
        </section>

        <section style={{ display: "grid", gap: 16, alignContent: "start" }}>
          {(status || error) && (
            <div
              style={{
                ...panelStyle,
                borderColor: error ? "#fecaca" : "#bbf7d0",
                background: error ? "#fff7f7" : "#f7fff9",
                color: error ? "#991b1b" : "#166534",
              }}
            >
              {error || status}
            </div>
          )}

          <div style={panelStyle}>
            <h2 style={{ margin: "0 0 16px", fontSize: 15 }}>
              Matching users
            </h2>
            {searchResults.length === 0 ? (
              <div style={smallTextStyle}>
                Search returns users with recent login and support sessions.
              </div>
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                {searchResults.map((user) => {
                  const selected = selectedUser?.id === user.id;
                  return (
                    <div
                      key={user.id}
                      style={{
                        border: selected ? "1px solid #111827" : "1px solid #e1e5ea",
                        borderRadius: 8,
                        padding: 12,
                        background: selected ? "#f8fafc" : "#fff",
                        display: "grid",
                        gap: 10,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 12,
                          alignItems: "start",
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 800 }}>
                            {user.email || user.username}
                          </div>
                          <div style={smallTextStyle}>
                            {user.username}#{user.discriminator} · {user.id}
                          </div>
                          <div style={smallTextStyle}>
                            {user.isActive ? "active" : "inactive"} ·{" "}
                            {user.membershipType || "no membership"} · updated{" "}
                            {formatDateTime(user.updateDate)}
                          </div>
                        </div>
                        <button
                          style={selected ? buttonStyle : secondaryButtonStyle}
                          onClick={() => selectUser(user)}
                        >
                          {selected ? "Selected" : "Select"}
                        </button>
                      </div>

                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {user.auths.map((auth) => (
                          <span
                            key={auth.id}
                            style={{
                              border: "1px solid #d6d9df",
                              borderRadius: 999,
                              padding: "3px 8px",
                              fontSize: 11,
                              color: "#374151",
                            }}
                          >
                            {auth.authType}
                            {auth.authIdentifier ? ` · ${auth.authIdentifier}` : ""}
                          </span>
                        ))}
                      </div>

                      {user.recentSessions.length > 0 && (
                        <div style={{ display: "grid", gap: 6 }}>
                          {user.recentSessions.map((session) => (
                            <div
                              key={session.sessionId}
                              style={{
                                display: "grid",
                                gridTemplateColumns: "minmax(0, 1fr) auto",
                                gap: 8,
                                alignItems: "center",
                                borderTop: "1px solid #eef1f5",
                                paddingTop: 6,
                              }}
                            >
                              <div style={smallTextStyle}>
                                <strong style={{ color: "#374151" }}>
                                  {session.isSupportImpersonation
                                    ? "support"
                                    : session.authType || "session"}
                                </strong>{" "}
                                · {session.sessionId}
                                <br />
                                {describeSession(session)}
                              </div>
                              <div style={{ ...smallTextStyle, textAlign: "right" }}>
                                {formatDateTime(session.lastUsedAt)}
                                {session.revokedAt ? " · revoked" : ""}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div style={panelStyle}>
            <h2 style={{ margin: "0 0 16px", fontSize: 15 }}>
              Target session
            </h2>
            {!result ? (
              <div style={{ fontSize: 13, color: "#6b7280" }}>
                Create a target session to show the access token here.
              </div>
            ) : (
              <div style={{ display: "grid", gap: 14 }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                    gap: 10,
                  }}
                >
                  <div style={{ fontSize: 12 }}>
                    <strong>User</strong>
                    <div style={{ color: "#4b5563", marginTop: 4 }}>
                      {result.targetEmail || result.targetUserId}
                    </div>
                  </div>
                  <div style={{ fontSize: 12 }}>
                    <strong>Session</strong>
                    <div style={{ color: "#4b5563", marginTop: 4 }}>
                      {result.sessionId}
                    </div>
                  </div>
                  <div style={{ fontSize: 12 }}>
                    <strong>Expires</strong>
                    <div style={{ color: "#4b5563", marginTop: 4 }}>
                      {targetTokenExpiry || result.expiresAt}
                    </div>
                  </div>
                </div>
                <textarea
                  readOnly
                  value={result.accessToken}
                  style={{ ...inputStyle, minHeight: 132, fontFamily: "monospace" }}
                />
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button
                    style={buttonStyle}
                    onClick={() => copyText(result.accessToken)}
                  >
                    Copy access token
                  </button>
                  <button
                    style={secondaryButtonStyle}
                    onClick={() =>
                      copyText(`Authorization: Bearer ${result.accessToken}`)
                    }
                  >
                    Copy auth header
                  </button>
                  <button
                    style={secondaryButtonStyle}
                    onClick={() => copyText(JSON.stringify(result, null, 2))}
                  >
                    Copy JSON
                  </button>
                </div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  Use the browser handoff snippet for setup work. It installs the
                  access and refresh token together so the app can refresh until
                  the support session expires.
                </div>
              </div>
            )}
          </div>

          {result && (
            <div style={panelStyle}>
              <h2 style={{ margin: "0 0 16px", fontSize: 15 }}>
                Browser handoff snippet
              </h2>
              <textarea
                readOnly
                value={authStateSnippet}
                style={{ ...inputStyle, minHeight: 120, fontFamily: "monospace" }}
              />
              <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                <button
                  style={secondaryButtonStyle}
                  onClick={() => copyText(authStateSnippet)}
                >
                  Copy snippet
                </button>
                <a
                  href="https://app.youwo.ai"
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    ...secondaryButtonStyle,
                    display: "inline-block",
                    textDecoration: "none",
                  }}
                >
                  Open app
                </a>
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
